import React, { useState, useEffect, useMemo } from "react";
import { Heart, X, MapPin, Sparkles, Mic, Image, MessageSquare, Calendar, Share2 } from "lucide-react";
import { useAuthContext } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface LoveMemory {
  id: string;
  user_id: string;
  memory_type: "pop" | "match" | "conversation" | "resonance" | "echo" | "love_note";
  booth_id?: string;
  booth_name?: string;
  city_name?: string;
  country_name?: string;
  mood?: string;
  voice_note_url?: string;
  photo_url?: string;
  shared_phrase?: string;
  cultural_exchange?: string;
  matched_with_id?: string;
  created_at: string;
  source_context?: any;
}

interface LoveMemoryGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  boothId: string;
  boothName: string;
  eventEndsAt?: string;
}

const LoveMemoryGallery: React.FC<LoveMemoryGalleryProps> = ({ isOpen, onClose, boothId, boothName, eventEndsAt }) => {
  const { user } = useAuthContext();
  const [memories, setMemories] = useState<LoveMemory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMemory, setSelectedMemory] = useState<LoveMemory | null>(null);

  // Fetch memories from database and localStorage
  useEffect(() => {
    if (!isOpen || !user) return;

    const fetchMemories = async () => {
      setLoading(true);
      try {
        // Fetch matches with exhibition context
        const { data: matches } = await supabase
          .from("matches")
          .select("id, user1_id, user2_id, created_at, match_source, source_context")
          .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
          .eq("match_source", "exhibition")
          .order("created_at", { ascending: false })
          .limit(50);

        // Fetch pops with exhibition context (if stored)
        const { data: pops } = await (supabase
          .from("pops") as any)
          .select("id, popper_id, popped_id, created_at, source_context")
          .or(`popper_id.eq.${user.id},popped_id.eq.${user.id}`)
          .order("created_at", { ascending: false })
          .limit(50);

        // Transform to LoveMemory format
        const memoryList: LoveMemory[] = [];

        matches?.forEach((match) => {
          const otherUserId = match.user1_id === user.id ? match.user2_id : match.user1_id;
          const ctx = match.source_context as any;
          if (ctx?.booth_id === boothId) {
            memoryList.push({
              id: match.id,
              user_id: user.id,
              memory_type: "match",
              booth_id: ctx?.booth_id || boothId,
              booth_name: ctx?.booth_name || boothName,
              city_name: ctx?.city_name,
              country_name: ctx?.country_name,
              matched_with_id: otherUserId,
              created_at: match.created_at,
              source_context: match.source_context,
            });
          }
        });

        pops?.forEach((pop) => {
          const ctx = pop.source_context as any;
          if (ctx?.booth_id === boothId) {
            memoryList.push({
              id: pop.id,
              user_id: user.id,
              memory_type: "pop",
              booth_id: ctx?.booth_id || boothId,
              booth_name: ctx?.booth_name || boothName,
              city_name: ctx?.city_name,
              country_name: ctx?.country_name,
              created_at: pop.created_at,
              source_context: pop.source_context,
            });
          }
        });

        // Load love notes from database
        const { data: loveNotes } = await (supabase as any)
          .from("exhibition_love_notes")
          .select("*")
          .eq("user_id", user.id)
          .eq("booth_id", boothId)
          .order("created_at", { ascending: false })
          .limit(50);

        loveNotes?.forEach((note) => {
          memoryList.push({
            id: note.id,
            user_id: note.user_id,
            memory_type: "love_note",
            booth_id: note.booth_id,
            booth_name: note.booth_name,
            city_name: note.city_name,
            country_name: note.country_name,
            mood: note.mood,
            voice_note_url: note.voice_note_url,
            photo_url: note.photo_url,
            shared_phrase: note.shared_phrase,
            cultural_exchange: note.cultural_exchange,
            created_at: note.created_at,
          });
        });

        // Sort by date
        memoryList.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        setMemories(memoryList);
      } catch (error) {
        console.error("Error fetching memories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMemories();
  }, [isOpen, user, boothId, boothName]);

  // Group memories by date
  const groupedMemories = useMemo(() => {
    const groups: Record<string, LoveMemory[]> = {};
    memories.forEach((memory) => {
      const date = new Date(memory.created_at).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
      if (!groups[date]) groups[date] = [];
      groups[date].push(memory);
    });
    return groups;
  }, [memories]);

  // Generate shareable reel (placeholder - would need actual video generation)
  const handleShareReel = (memory: LoveMemory) => {
    // TODO: Generate actual reel with animations, soundscape, etc.
    toast.success("Reel generation coming soon! 🎬");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-rose-50 to-pink-50">
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Heart className="w-6 h-6 text-rose-500" />
              Love Memory Gallery
            </h2>
            <p className="text-sm text-muted-foreground mt-1">{boothName}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4" />
              <p className="text-muted-foreground">Loading memories...</p>
            </div>
          ) : memories.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-16 h-16 text-rose-200 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No memories yet</h3>
              <p className="text-sm text-muted-foreground">
                Your love story moments will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedMemories).map(([date, dateMemories]) => (
                <div key={date} className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    {date}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {dateMemories.map((memory) => (
                      <div
                        key={memory.id}
                        onClick={() => setSelectedMemory(memory)}
                        className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-4 border border-rose-200/50 cursor-pointer hover:shadow-lg transition-all"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {memory.memory_type === "match" && <Heart className="w-5 h-5 text-red-500" />}
                            {memory.memory_type === "pop" && <Sparkles className="w-5 h-5 text-pink-500" />}
                            {memory.memory_type === "love_note" && <MessageSquare className="w-5 h-5 text-rose-500" />}
                            <span className="text-sm font-semibold text-foreground capitalize">
                              {memory.memory_type.replace("_", " ")}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareReel(memory);
                            }}
                          >
                            <Share2 className="w-4 h-4" />
                          </Button>
                        </div>

                        {memory.photo_url && (
                          <img
                            src={memory.photo_url}
                            alt="Memory"
                            className="w-full h-32 object-cover rounded-lg mb-2"
                          />
                        )}

                        {memory.shared_phrase && (
                          <p className="text-sm text-foreground mb-2 italic">"{memory.shared_phrase}"</p>
                        )}

                        {memory.mood && (
                          <div className="text-xs text-muted-foreground mb-2">
                            Mood: <span className="font-semibold">{memory.mood}</span>
                          </div>
                        )}

                        {memory.city_name && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            {memory.city_name}
                            {memory.country_name && `, ${memory.country_name}`}
                          </div>
                        )}

                        {memory.voice_note_url && (
                          <audio src={memory.voice_note_url} controls className="w-full mt-2" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        {selectedMemory && (
          <div className="px-6 py-4 border-t bg-rose-50">
            <Button onClick={() => handleShareReel(selectedMemory)} className="w-full">
              <Share2 className="w-4 h-4 mr-2" />
              Share Our Spark (Generate Reel)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoveMemoryGallery;

